import { useEffect, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router';

import { ChartProvider } from '@/components/Chart/context/ChartContext';
import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import { FACTSHEET_TAB_PARAM_NAME, FactsheetDetails } from '@/pages/Facthsheets/constants';
import ApproachTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ApproachTab';
import ChartTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ChartTab';
import CriteriaTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/CriteriaTab';
import DetailsTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/DetailsTab';
import FlowchartTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/FlowchartTab';
import ItemsTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ItemsTab';
import RelatedCodesTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/RelatedCodesTab';
import UnregisteredBanner from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/UnregisteredBanner';
import { useGxTokenFromStorage } from '@/services/storage';

const QualitySpecTab = ({
  factsheet,
  gxToken
}: {
  factsheet: FactsheetDetails;
  gxToken: string | null;
}) => {
  let qualitySpecParsedLines: string[] = [];
  if (
    factsheet.factsheetVersionHistory.length > 0 &&
    factsheet.factsheetVersionHistory[0].qualitySpecification
  ) {
    qualitySpecParsedLines =
      factsheet.factsheetVersionHistory[0]?.qualitySpecification?.split('\n');
  }

  return gxToken ? (
    <Box>
      {qualitySpecParsedLines.map(line => {
        return (
          <Typography
            key={line}
            sx={{
              fontSize: 16,
              fontWeight: 500,
              mb: 0.5
            }}
          >
            {line}
          </Typography>
        );
      })}
    </Box>
  ) : (
    <Box sx={{ pt: 5 }}>
      <UnregisteredBanner />
    </Box>
  );
};

const FactsheetBody = ({
  factsheet,
  openNewTab
}: {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const gxToken = useGxTokenFromStorage();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const contentTabs = useMemo(() => {
    const tabs = [
      {
        position: 1,
        name: 'Details',
        component: (
          <DetailsTab
            factsheet={factsheet}
            gxToken={gxToken}
          />
        )
      },
      {
        position: 4,
        name: 'Approach',
        component: <ApproachTab factsheet={factsheet} />
      },
      {
        position: 99,
        name: 'Related codes',
        component: (
          <RelatedCodesTab
            factsheet={factsheet}
            openNewTab={openNewTab}
          />
        )
      }
    ];

    if (factsheet.spotSymbols.length > 0) {
      tabs.unshift({
        position: 0,
        name: 'Chart',
        component: (
          <ChartProvider>
            <ChartTab factsheet={factsheet} />
          </ChartProvider>
        )
      });
    }

    if (
      factsheet.factsheetVersionHistory.length > 0 &&
      factsheet.factsheetVersionHistory[0].qualitySpecification
    ) {
      tabs.unshift({
        position: 2,
        name: 'Quality spec',
        component: (
          <QualitySpecTab
            factsheet={factsheet}
            gxToken={gxToken}
          />
        )
      });
    }

    if (
      factsheet.factsheetVersionHistory.length > 0 &&
      factsheet.factsheetVersionHistory[0].criteriaForInclusion
    ) {
      tabs.unshift({
        position: 3,
        name: 'Criteria',
        component: <CriteriaTab factsheet={factsheet} />
      });
    }

    tabs.unshift({
      position: 5,
      name: 'Flowchart',
      component: (
        <FlowchartTab
          factsheet={factsheet}
          openNewTab={openNewTab}
        />
      )
    });

    if (factsheet.symbols.length > 0) {
      tabs.unshift({
        position: 6,
        name: 'Available items',
        component: <ItemsTab factsheet={factsheet} />
      });
    }

    return tabs.toSorted((a, b) => a.position - b.position);
  }, [factsheet, gxToken, openNewTab]);

  const setActiveTabParam = (value: number) => {
    const tabName = contentTabs.find((_, index) => index === value)?.name;
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set(FACTSHEET_TAB_PARAM_NAME, tabName ?? '');
    setUrlSearchParams(newSearchParams);
  };
  useEffect(() => {
    const activeTabParam = decodeURIComponent(urlSearchParams.get(FACTSHEET_TAB_PARAM_NAME) ?? '');
    if (activeTabParam?.length) {
      const index = contentTabs.findIndex(tab => tab.name === activeTabParam);
      setActiveTab(index);
    }
  }, [contentTabs, urlSearchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        flex: 1,
        px: 3,
        flexGrow: 1,
        overflow: 'hidden',
        width: '100%'
      }}
    >
      <Box
        sx={{
          mt: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Tabs
          activeTab={activeTab}
          scrollButtons='auto'
          tabs={contentTabs.map(({ name }) => ({ name }))}
          onChange={setActiveTabParam}
        />
      </Box>
      {contentTabs.map((tab, index) => (
        <TabPanel
          key={factsheet.code + index}
          containerSX={{
            flex: 1,
            flexGrow: 1,
            width: '100%',
            overflow: 'hidden',
            ...(index === activeTab ? { display: 'flex' } : {})
          }}
          index={index}
          sx={{
            flex: 1,
            flexGrow: 1,
            width: '100%',
            overflow: 'auto'
          }}
          value={activeTab}
        >
          {(gxToken || tab.position === 1) && tab.component}
          {!gxToken && <UnregisteredBanner />}
        </TabPanel>
      ))}
    </Box>
  );
};
export default FactsheetBody;
